import { type BaseRendererComponentProps, BaseModel, BaseModelProps } from '@mail/3promo';
import { ReactRenderer } from '@mail/3promo/render/react';
import { LocalStorageStore } from '@mail/3promo/stores/localStorage';
import type { ModelCreator } from '@mail/3promo/types';
import React from 'react';
import { IS_MOBILE_OR_TABLET_BROWSER, IS_WEBVIEW, IS_WEBVIEW_FROM_UA } from 'reactApp/appHelpers/configHelpers';
import {
    DUPLICATES_ONBOARDING_MODAL_CONFIG,
    DUPLICATES_ONBOARDING_MODAL_ID,
    IS_DUPLICATES_ENABLED,
} from 'reactApp/appHelpers/featuresHelpers/features/duplicates';
import { getRemoveAllDuplicatesTooltipShown } from 'reactApp/modules/duplicates/duplicates.helpers';
import { getUserHasDuplicates } from 'reactApp/modules/duplicates/duplicates.selectors';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { SettingsSelectors } from 'reactApp/modules/settings/settings.selectors';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { isCloudOrMailOrApiOverQuota } from 'reactApp/modules/userQuota/overquota.helpers';
import { store as reduxStore } from 'reactApp/store';
import { sendXray } from 'reactApp/utils/ga';

class PopupModel extends BaseModel {
    getParent(): HTMLElement | null {
        return document.documentElement;
    }
}

const LazyComponent = React.lazy(() => import('reactApp/ui/Duplicates/DuplicatesOnboardingModal/DuplicatesOnboardingModal'));

const SuspendedComponent = <P extends BaseRendererComponentProps>(props: P) => {
    return (
        <React.Suspense fallback={null}>
            <LazyComponent {...props} />
        </React.Suspense>
    );
};

export const duplicatesOnboardingModalCreator: ModelCreator = ({ models, render, stores }) => {
    return new PopupModel(
        DUPLICATES_ONBOARDING_MODAL_ID,
        models.base,
        new BaseModelProps(DUPLICATES_ONBOARDING_MODAL_ID, {
            store: new LocalStorageStore(DUPLICATES_ONBOARDING_MODAL_ID, stores.localStorage),
            renderer: new ReactRenderer(DUPLICATES_ONBOARDING_MODAL_ID, SuspendedComponent, render.react),
            closable: true,
            showLimit: DUPLICATES_ONBOARDING_MODAL_CONFIG.showLimit,
            closeLimit: DUPLICATES_ONBOARDING_MODAL_CONFIG.closeLimit,
            showInterval: DUPLICATES_ONBOARDING_MODAL_CONFIG.showInterval,
            templateData: {},
            ignoreTestOnRedraw: true,
            markAsCompleted: true,
            hideOnCompleted: true,
            hideOnSnooze: true,
            test: (resolve, reject) => {
                if (IS_MOBILE_OR_TABLET_BROWSER || IS_WEBVIEW || IS_WEBVIEW_FROM_UA || !IS_DUPLICATES_ENABLED) {
                    reject();
                    return;
                }

                const state = reduxStore.getState();
                const storage = getCurrentStorage(state);
                const isTooltipShown = getRemoveAllDuplicatesTooltipShown();
                if (storage !== EStorageType.duplicates || !isTooltipShown) {
                    reject();
                    return;
                }

                const { action = '' } = SettingsSelectors.getQueryParams(state);
                if (action) {
                    reject();
                    sendXray(['promo-test', DUPLICATES_ONBOARDING_MODAL_ID, 'rejectbyaction']);
                    return;
                }

                const isBizUser = UserSelectors.isBizUser(state);
                const isB2BUser = UserSelectors.isB2BUser(state);
                const isPaidUser = UserSelectors.isPaidUser(state);
                const isCorpUser = UserSelectors.isCorpUser(state);
                const isOverquota = isCloudOrMailOrApiOverQuota(state);
                const hasDuplicates = getUserHasDuplicates(state);

                const isReject = isPaidUser || isBizUser || isB2BUser || isOverquota || isCorpUser || !hasDuplicates;

                sendXray(['promo-test', DUPLICATES_ONBOARDING_MODAL_ID, isReject ? 'reject' : 'resolve']);

                if (isReject) {
                    reject();
                    return;
                }

                resolve();
            },
        })
    );
};
